/**
 * FundsWidget
 *
 * Compact broker funds display for the nav / sidebar / top bar.
 * Shows available margin, used margin, and total balance.
 * Refreshes every 5 minutes automatically.
 * Shows a re-auth prompt if the session is expired.
 *
 * Usage — in your sidebar or top nav:
 *   import { FundsWidget } from './pages/broker/FundsWidget';
 *   <FundsWidget />                        // default (horizontal, compact)
 *   <FundsWidget variant="vertical" />     // stacked layout for sidebar
 *   <FundsWidget variant="minimal" />      // single "₹XX,XXX available" pill
 *
 * MIGRATION NOTE:
 *   Replace the brokerApi import path if your project structure differs.
 */
import { useQuery } from '@tanstack/react-query';
import { brokerApi } from '../../api/brokerClient';
import { useSessionStore } from '../../session/store';

const fmt = (v) =>
  v == null
    ? '—'
    : '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });

function RefreshIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd"
        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
        clipRule="evenodd" />
    </svg>
  );
}

// ── Horizontal (default) ──────────────────────────────────────────────────────

function HorizontalWidget({ funds, status, onRefresh, isRefreshing }) {
  if (!funds) return null;
  return (
    <div className="flex items-center gap-4 px-3 py-1.5 bg-bg-surface border border-bg-border rounded-lg text-sm">
      <span className="text-xs text-txt-muted font-medium">
        {status?.active_broker || 'Broker'}
      </span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-txt-secondary">Avail</span>
        <span className="font-semibold text-txt-primary">{fmt(funds.available_cash)}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-txt-secondary">Used</span>
        <span className="font-medium text-txt-secondary">{fmt(funds.used_margin)}</span>
      </div>
      <button
        onClick={onRefresh}
        title="Refresh funds"
        className="ml-1 text-txt-muted hover:text-info transition-colors"
      >
        <RefreshIcon className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
}

// ── Vertical (sidebar) ────────────────────────────────────────────────────────

function VerticalWidget({ funds, status, onRefresh, isRefreshing }) {
  if (!funds) return null;
  return (
    <div className="bg-bg-surface border border-bg-border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-txt-secondary uppercase tracking-wide">
          {status?.active_broker || 'Broker'} Funds
        </span>
        <button
          onClick={onRefresh}
          title="Refresh"
          className="text-txt-muted hover:text-info transition-colors"
        >
          <RefreshIcon className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {[
        { label: 'Available', value: funds.available_cash, accent: 'text-bull' },
        { label: 'Used',      value: funds.used_margin,   accent: 'text-bear'   },
        { label: 'Total',     value: funds.total_balance, accent: 'text-txt-primary'  },
      ].map(({ label, value, accent }) => (
        <div key={label} className="flex items-center justify-between">
          <span className="text-xs text-txt-secondary">{label}</span>
          <span className={`text-sm font-semibold ${accent}`}>{fmt(value)}</span>
        </div>
      ))}
      {funds.fetched_at && (
        <p className="text-[10px] text-txt-muted text-right">
          Updated {new Date(funds.fetched_at).toLocaleTimeString('en-IN', {
            hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
          })} IST
        </p>
      )}
    </div>
  );
}

// ── Minimal pill ──────────────────────────────────────────────────────────────

function MinimalWidget({ funds, status }) {
  if (!funds) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs h-10 px-3 border border-bull/20 text-bull bg-bull/5 rounded-md font-medium shrink-0">
      <span className="w-1.5 h-1.5 rounded-full bg-bull" />
      {fmt(funds.available_cash)} avail · {status?.active_broker || ''}
    </span>
  );
}

// ── Funds unavailable (operational, non-expiry) state ─────────────────────────
//
// An ACTIVE broker whose live funds probe failed or returned an unusable
// payload (including a legitimate `200 OK` body of null — see
// brokerClient.js's shared getFunds validation). This must stay visually and
// textually distinct from ExpiredState below: it never implies the stored
// token/session itself expired or needs re-authentication.

function FundsUnavailableState({ activeBroker, variant }) {
  if (variant === 'minimal') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs h-10 px-3 border border-bg-border text-txt-muted bg-bg-hover/20 rounded-md font-medium shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-txt-muted" />
        Funds unavailable
      </span>
    );
  }
  if (variant === 'vertical') {
    return (
      <div className="bg-bg-surface border border-bg-border rounded-lg p-3 flex items-center gap-2 text-xs text-txt-muted">
        <span className="w-1.5 h-1.5 rounded-full bg-txt-muted flex-shrink-0" />
        <span>{activeBroker || 'Broker'} — Funds unavailable</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-bg-hover/40 border border-bg-border rounded-lg text-xs text-txt-muted">
      <span className="w-1.5 h-1.5 rounded-full bg-txt-muted flex-shrink-0" />
      <span>{activeBroker || 'Broker'} — Funds unavailable</span>
    </div>
  );
}

// ── Expired / no broker states ────────────────────────────────────────────────

function ExpiredState({ tokenStatus, activeBroker, variant }) {
  if (variant === 'minimal') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs h-10 px-3 border border-amber/35 text-amber bg-amber/10 rounded-md font-medium shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-amber" />
        Broker token expired
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber/10 border border-amber/35 rounded-lg text-xs text-amber">
      <span className="w-1.5 h-1.5 rounded-full bg-amber flex-shrink-0" />
      <span>
        {activeBroker || 'Broker'} token expired —
        {' '}<a href="/broker/connect" className="underline font-medium">Re-authenticate</a>
      </span>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function FundsWidget({ variant = 'horizontal' }) {
  const currentUserId = useSessionStore((s) => s.user?.id);
  const {
    data: status,
  } = useQuery({
    queryKey: ['brokerStatus', currentUserId ?? null],
    queryFn:  brokerApi.getCurrent,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const {
    data:      funds,
    isLoading: fundsLoading,
    isError:   fundsUnavailable,
    refetch:   refetchFunds,
    isFetching: isFetching,
  } = useQuery({
    queryKey: ['brokerFunds', currentUserId ?? null],
    queryFn:  brokerApi.getFunds,
    // Only fetch if there's an active broker with a live session
    enabled:  status?.has_active_broker && status?.token_status === 'ACTIVE',
    // Refresh every 5 minutes
    refetchInterval: 5 * 60 * 1000,
    staleTime:       4 * 60 * 1000,
  });

  const tokenStatus  = status?.token_status || 'NO_BROKER';
  const activeBroker = status?.active_broker;

  // Nothing to show if no broker connected
  if (tokenStatus === 'NO_BROKER' || !activeBroker) return null;

  // Show expired state if the stored backend lifecycle itself is stale.
  // This is the sole re-auth/token-expired signal; a merely unavailable
  // funds probe below (fundsUnavailable) must never say this.
  if (tokenStatus === 'EXPIRED' || tokenStatus === 'NOT_SET') {
    return (
      <ExpiredState
        tokenStatus={tokenStatus}
        activeBroker={activeBroker}
        variant={variant}
      />
    );
  }

  // Loading skeleton
  if (fundsLoading && !funds) {
    return (
      <div className="animate-pulse flex gap-3 px-3 py-1.5 bg-bg-hover rounded-lg">
        {[1, 2].map((i) => (
          <div key={i} className="h-4 bg-bg-elevated rounded w-20" />
        ))}
      </div>
    );
  }

  // An ACTIVE broker whose funds probe failed/returned an unusable payload
  // (including the shared getFunds 200/null validation). Checked before any
  // funds-shaped rendering below so a failed refetch can never keep showing
  // previously cached data as if the latest health check succeeded.
  if (fundsUnavailable) {
    return <FundsUnavailableState activeBroker={activeBroker} variant={variant} />;
  }

  const props = { funds, status, onRefresh: refetchFunds, isRefreshing: isFetching };

  if (variant === 'vertical')  return <VerticalWidget  {...props} />;
  if (variant === 'minimal')   return <MinimalWidget   funds={funds} status={status} />;
  return                              <HorizontalWidget {...props} />;
}
